import React, {useEffect, useMemo, useState} from 'react';
import styles from './PersonContacts.module.css';
import {ApiError} from '@/services/api';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling.ts';
import {
  Button,
  ButtonVariant,
  EmptyState,
  ErrorHappened,
  FormItem,
  InputType,
  Loading,
  ModalSize,
  useModal
} from '@fs/uikit-react';
import {Contact, ContactType} from '@/shared/model/contact.ts';
import {ContactApi} from '@/pages/Operator/Person/api/ContactApi.ts';
import ContactModal from './modals/ContactModal/ContactModal.tsx';
import ContactDeleteModal
  from './modals/ContactDeleteModal/ContactDeleteModal.tsx';
import CanIcon from "@/assets/icons/CanIcon.tsx";

interface PersonContactsProps {
  personId?: string;
}

const PersonContacts: React.FC<PersonContactsProps> = ({personId}) => {
  const { openModal } = useModal();

  const [items, setItems] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchContacts = async () => {
    if (!personId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await ContactApi.getByPersonId(personId);
      setItems(response.data);
    } catch (err) {
      setError(err as ApiError);
      console.error('Error fetching contacts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  const handleCreate = () => {
    if (!personId) return;
    openModal(
      ModalSize.SMALL,
      'Додати контакт',
      <ContactModal personId={personId} onSuccess={fetchContacts} />
    );
  };

  const handleEdit = (contact: Contact) => {
    if (!personId || !contact.id) return;
    openModal(
      ModalSize.SMALL,
      'Редагувати контакт',
      <ContactModal personId={personId} contactId={contact.id} onSuccess={fetchContacts} />
    );
  };

  const handleDelete = (contact: Contact) => {
    if (!contact.id) return;
    openModal(
      ModalSize.SMALL,
      'Видалити контакт',
      <ContactDeleteModal contact={contact} onSuccess={fetchContacts} />
    );
  };

  const renderContactValue = (c: Contact): {label: string, value: string} => {
    switch (c.type) {
      case ContactType.PHONE:
        return {label: 'Телефон', value: c.phoneNumber};
      case ContactType.EMAIL:
        return {label: 'Email', value: c.email};
      case ContactType.LINK: {
        const cat = c.category && 'name' in c.category ? `${c.category.name}` : '';
        return {label: `${cat}`, value: c.url};
      }
      case ContactType.ADDRESS: {
        const parts = [c.country, c.city, c.addressLine1, c.addressLine2, c.postalCode].filter(Boolean);
        return {label: 'Адреса', value: parts.join(', ')};
      }
      case ContactType.MESSENGER:
        return {label: `${c.name}`, value: c.username};
      default:
        return {label: 'Контакт', value: ''};
    }
  };

  const list = useMemo(() => {
    if (!items) return [];

    const priority: { [key in ContactType]: number } = {
      [ContactType.EMAIL]: 0,
      [ContactType.PHONE]: 1,
      [ContactType.LINK]: 2,
      [ContactType.MESSENGER]: 3,
      [ContactType.ADDRESS]: 4,
    };

    return [...items].sort((a, b) => {
      const pa = priority[a.type as ContactType] ?? 999;
      const pb = priority[b.type as ContactType] ?? 999;
      if (pa !== pb) return pa - pb;
      return 0;
    });
  }, [items]);

  if (isLoading) {
    return <Loading text="Завантаження контактів..."/>;
  }

  if (error) {
    return <ErrorHappened text={getUserFriendlyErrorMessage(error)}/>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.actions}>
        <Button title="Додати" onClick={handleCreate} />
      </div>
      {!list || list.length === 0 ? (
        <EmptyState text="Немає контактів" />
      ) : (
        <div className={styles.grid}>
          {list.map((c) => {
            const {label, value} = renderContactValue(c);
            return (
              <div key={c.id} className={styles.item}>
                <div className={styles.itemMain} onClick={() => handleEdit(c)}>
                  <FormItem
                    label={label}
                    type={InputType.TEXT}
                    value={value}
                  />
                </div>
                <div className={styles.itemActions}>
                  <Button
                    icon={<CanIcon />}
                    variant={ButtonVariant.DESTRUCTIVE}
                    onClick={() => handleDelete(c)}
                    className={styles.deleteButton}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PersonContacts;
